/**
 * Shared, idempotent Google Maps (Places library) loader.
 *
 * The Maps JS script is normally only loaded lazily by `GooglePlacesAddress`
 * when it mounts - fine for pages with an address field, but it means
 * anything that needs `google.maps.places` WITHOUT that component on screen
 * (e.g. Routy's search_places tool, usable from any page in the app) would
 * find `window.google` undefined and fail immediately. This gives any such
 * caller a way to self-load (or wait for an in-flight/existing load) the
 * script on demand, regardless of what else is on the page.
 */
import { config } from '../config/config';

let loadPromise: Promise<void> | null = null;

function placesReady(): boolean {
  return !!window.google?.maps?.places?.PlacesService;
}

/** Resolves once `google.maps.places` is usable, loading the script if needed. */
export function ensureGoogleMapsLoaded(): Promise<void> {
  if (placesReady()) return Promise.resolve();
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve, reject) => {
    const pollForReady = (onTimeout: () => void, maxAttempts = 100) => {
      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if (placesReady()) {
          clearInterval(interval);
          resolve();
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          onTimeout();
        }
      }, 100);
    };

    const existingScript = document.querySelector<HTMLScriptElement>('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      // Someone else (e.g. GooglePlacesAddress) is already loading it - just wait.
      pollForReady(() => reject(new Error('Google Maps took too long to load.')));
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${config.googleMapsApiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      // The places namespace can attach a beat after the load event fires.
      pollForReady(() => reject(new Error('Google Maps loaded but the Places library never became available.')), 50);
    };
    script.onerror = () => reject(new Error('Failed to load the Google Maps script.'));
    document.head.appendChild(script);
  }).catch((err) => {
    // Don't cache a failed load - let the next call retry from scratch.
    loadPromise = null;
    throw err;
  });

  return loadPromise;
}
